import React, { ReactElement, useEffect, useMemo, useState } from 'react'
// import ReCAPTCHA from 'react-google-recaptcha'
import {
    Box,
    Button,
    TextField,
    Typography,
    Container,
    CircularProgress,
} from '@mui/material'
import { useAppDispatch, useAppSelector } from '../../../redux/hook'
import { updateLoader } from '../../../redux/slices/loaderSlice'
import { IShipment } from './interfaces'
import { getShipmentIdbyTripNumber } from '../../../services/guestServices'
import { useNavigate, useLocation } from 'react-router-dom'
import ShipmentListingGuest from './ShipmentsListingGuest'
import { useTranslation } from 'react-i18next'
import {
    setPublicSearchShipments,
    setTripNumberInStore,
} from '../../../redux/slices/inTransitSlice'

const ShipmentSearchGuest = (): ReactElement => {
    const { t } = useTranslation()
    const location = useLocation()
    const queryParams = new URLSearchParams(location.search)
    const tripNumberFromStore = useAppSelector(
        (state) => state.inTransit.tripNumber,
    )
    const [tripNumber, setTripNumber] = useState(tripNumberFromStore)
    // const [recaptchaValue, setRecaptchaValue] = useState<string | null>(null)
    const [errorMessage, setErrorMessage] = useState<string | null>(null)
    const [isLoading, setIsLoading] = useState<boolean>(false)
    // const sitekey = import.meta.env.VITE_GOOGLE_RECAPTCHA_SITE_KEY || ''
    const dispatch = useAppDispatch()
    const navigate = useNavigate()
    const shipments = useAppSelector(
        (state) => state.inTransit.publicSearchShipments,
    )

    const enabledShipments = useMemo(
        () => shipments.filter((shipment) => shipment.publicSearchEnabled),
        [shipments],
    )

    const handleShipmentIdChange = (
        event: React.ChangeEvent<HTMLInputElement>,
    ) => {
        setTripNumber(event.target.value)
    }

    // const handleRecaptchaChange = (value: string | null) => {
    //     setRecaptchaValue(value)
    // }

    const getShipments = async (tripNumber: string) => {
        setIsLoading(true)
        try {
            const shipments: IShipment[] =
                await getShipmentIdbyTripNumber(tripNumber)
            dispatch(setPublicSearchShipments(shipments))
            if (shipments && shipments.length === 1) {
                if (!shipments[0].publicSearchEnabled) {
                    setErrorMessage(t('publicSearchDisabled'))
                    return
                }
                navigate(`/guestUser/shipment/${shipments[0].shipmentId}`, {
                    state: { shipment: shipments[0] },
                })
                return
            }
        } catch (error: unknown) {
            const errorMessage =
                error instanceof Error
                    ? decodeURIComponent(error?.message)
                    : t('An error occurred')
            setErrorMessage(errorMessage)
            dispatch(setPublicSearchShipments([]))
        } finally {
            setIsLoading(false)
        }
    }

    const handleSubmit = (event: React.FormEvent) => {
        event.preventDefault()
        setErrorMessage(null) // Clear previous errors
        getShipments(tripNumber)
        dispatch(setTripNumberInStore(tripNumber))

        // if (recaptchaValue) {
        //     // Process the tripNumber and recaptchaValue
        //     console.log('Trip Number:', tripNumber)
        //     console.log('reCAPTCHA value:', recaptchaValue)
        //     // Add your search logic here
        // } else {
        //     setErrorMessage('Please complete the reCAPTCHA.')
        // }
    }

    useEffect(() => {
        /**
         * By default the app starts with loader as true. This is set to false
         * after the user is logged in and an org is set. For guest user these
         * steps are not followed. Hence we are setting false here.
         */
        dispatch(updateLoader(false))

        /**
         * Automatically set tripId and load shipment results if the search params contain tripId
         * This supports the search feature on the login page, which redirects here with `tripId` on submit.
         */
        const tripIdFromQuery = queryParams.get('tripId')
        if (tripIdFromQuery) {
            getShipments(tripIdFromQuery)
            setTripNumber(tripIdFromQuery)
            dispatch(setTripNumberInStore(tripIdFromQuery))
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    return (
        <Container
            maxWidth="sm"
            sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
            }}
        >
            <Box
                component="form"
                onSubmit={handleSubmit}
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 2,
                    p: 3,
                    border: '1px solid grey',
                    borderRadius: '4px',
                }}
            >
                <Typography variant="h4" component="h2">
                    {t('shipmentSearchTitle')}
                </Typography>
                <TextField
                    id="guest-shipmentsearch-input-tripid"
                    label={t('tripNumber')}
                    variant="outlined"
                    value={tripNumber}
                    onChange={handleShipmentIdChange}
                    required
                    fullWidth
                />
                <Box
                    sx={{
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                    }}
                >
                    {/* <ReCAPTCHA
                        sitekey={sitekey}
                        onChange={handleRecaptchaChange}
                    /> */}
                </Box>
                {errorMessage && (
                    <Typography color="error" variant="body2">
                        {errorMessage}
                    </Typography>
                )}
                <Button
                    id="guest-shipmentsearch-button-submit"
                    type="submit"
                    variant="contained"
                    color="primary"
                    // disabled={!recaptchaValue}
                    disabled={isLoading || !tripNumber}
                    endIcon={isLoading && <CircularProgress size={14} />}
                >
                    {t('search')}
                </Button>
            </Box>
            {enabledShipments && enabledShipments.length > 0 && (
                <Box sx={{ flexGrow: 1 }}>
                    <ShipmentListingGuest
                        shipments={enabledShipments}
                        isLoading={isLoading}
                    />
                </Box>
            )}
        </Container>
    )
}

export default ShipmentSearchGuest
